import { t as tr } from './i18n.js'
import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useLocation } from 'react-router'
import { api, type Me, type Role, type User } from './api.js'

interface AuthState {
  user: User | null
  mfa: Me['mfa']
  googleEnabled: boolean
  loading: boolean
  refresh: () => Promise<unknown>
  signOut: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/auth/me'), staleTime: 60_000 })
  const value: AuthState = {
    user: me.data?.user ?? null,
    mfa: me.data?.mfa ?? null,
    googleEnabled: me.data?.googleEnabled ?? false,
    loading: me.isPending,
    refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
    signOut: async () => {
      await api('/auth/logout', { method: 'POST' })
      qc.clear()
      await qc.invalidateQueries({ queryKey: ['me'] })
    },
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}

const RANK: Record<Role, number> = { viewer: 0, member: 1, manager: 2, admin: 3 }
export const atLeast = (user: User | null, role: Role) => !!user && RANK[user.role] >= RANK[role]

/** Route guard: bounce anonymous users to /login, remembering where they were. */
export function RequireAuth({ children, role }: { children: ReactNode; role?: Role }) {
  const { user, loading } = useAuth()
  const loc = useLocation()
  if (loading) return <p className="muted center">{tr("Loading…")}</p>
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />
  if (role && !atLeast(user, role)) return <p className="bad center">{tr("You do not have access to this page.")}</p>
  return <>{children}</>
}
